/**
 * Association-related components extracted from MappingStep.tsx.
 *
 * - AssociationMappingRow: a single cross-file association mapping (source file/col → target file/col).
 * - InlineAssociationsSection: same-row pairs + cross-file associations for the active file tab.
 * - CrmEntityAssociationRow: a single CRM entity lookup association row.
 * - InlineCrmEntityAssociationsSection: section for linking CSV columns to existing CRM records.
 */

import { useQuery } from '@tanstack/react-query';
import { Link01, Trash01 } from '@untitledui/icons';
import type { Key } from 'react';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { Button } from '@/lib/base/buttons/button';
import { IconButton } from '@/lib/base/buttons/button-utility';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { AssociationMatcher } from '@/lib/crm/hooks/useAssociationMatchers';
import type { CrmEntityAssociationMapping } from '@/lib/csv-import/api';

import type { ImportTypeOption } from '../../hooks/useImportTargetTypes';
import { useImportVocabulary } from '../../import-vocabulary';
import {
  CRM_ENTITY_LOOKUP_PROPERTIES,
  deriveLookupProperties,
  fetchPropertyTypes,
} from './mapping-constants';
import { SameRowPairCheckbox, ScrollableTableWrapper } from './mapping-shared';

// ============================================================================
// Constants
// ============================================================================

/** Stable empty lookup-property list for the no-target / loading state. */
const EMPTY_LOOKUP_PROPERTIES: Array<{ value: string; label: string }> = [];

/** Stable empty association-type list for the no-pair / loading state. */
const EMPTY_PAIR_TYPES: AssociationType[] = [];

// Target object types are now schema-driven (core types + the org's custom
// domain types) via useImportTargetTypes, threaded in as `targetTypeOptions`.
// Lookup ("match by") properties come from CRM_ENTITY_LOOKUP_PROPERTIES for the
// core types and are derived from the live property schema for custom types.

// ============================================================================
// InlineAssociationsSection
// ============================================================================

export interface InlineAssociationsSectionProps {
  /** Same-row pair checkbox sections relevant to this file. */
  sameRowPairs: Array<{
    pairKey: string;
    sourceLabel: string;
    targetLabel: string;
    isEnabled: boolean;
  }>;
  /** The real file ID for same-row pair toggle callbacks. */
  fileId: string | number;
  /** Callback for same-row toggle changes. */
  onSameRowToggleChange: (
    fileId: string | number,
    pairKey: string,
    isEnabled: boolean
  ) => void;
}

/**
 * Same-row relationship checkboxes for a multi-type file (two entities created
 * from one CSV row). Cross-file value-match associations were removed — those are
 * now configured per-column via the "Link to record" disclosure in the row.
 */
export function InlineAssociationsSection({
  sameRowPairs,
  fileId,
  onSameRowToggleChange,
}: InlineAssociationsSectionProps) {
  if (sameRowPairs.length === 0) {
    return null;
  }

  return (
    <div className='space-y-4 mt-6'>
      <div className='h-px bg-border-secondary' aria-hidden='true' />
      <h3 className='text-sm font-medium text-fg-primary flex items-center gap-2'>
        <Link01 className='size-4 text-fg-quaternary' />
        Associations
      </h3>
      <div className='space-y-2'>
        <div>
          <p className='text-caption text-fg-quaternary'>
            Same-row relationships
          </p>
          <p className='text-xs text-fg-tertiary mt-0.5'>
            Link records that share a CSV row.
          </p>
        </div>
        <div className='border border-border-secondary rounded-lg bg-bg-primary p-3 space-y-2'>
          {sameRowPairs.map(pair => (
            <SameRowPairCheckbox
              key={pair.pairKey}
              fileId={fileId}
              pairKey={pair.pairKey}
              sourceLabel={pair.sourceLabel}
              targetLabel={pair.targetLabel}
              isEnabled={pair.isEnabled}
              onChange={onSameRowToggleChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CrmEntityAssociationRow
// ============================================================================

export interface CrmEntityAssociationRowProps {
  index: number;
  mapping: CrmEntityAssociationMapping;
  sourceColumns: string[];
  /** Org id, for fetching custom-type lookup property schemas. */
  orgId: number;
  /** Schema-driven target entity types (core + custom domain types). */
  targetTypeOptions: ImportTypeOption[];
  onUpdate: (index: number, mapping: CrmEntityAssociationMapping) => void;
  onRemove: (index: number) => void;
}

export const CrmEntityAssociationRow = memo(function CrmEntityAssociationRow({
  index,
  mapping,
  sourceColumns,
  orgId,
  targetTypeOptions,
  onUpdate,
  onRemove,
}: CrmEntityAssociationRowProps) {
  // A source-driven import has fields, not columns — see `import-vocabulary`.
  const vocabulary = useImportVocabulary();

  const mappingRef = useRef(mapping);
  useLayoutEffect(() => {
    mappingRef.current = mapping;
  }, [mapping]);

  const targetType = mapping.target_entity_type_name;
  // Core types have a curated static lookup list; custom (org_*) types derive
  // theirs from the live property schema (only fetched when no static entry).
  const staticLookupProps = CRM_ENTITY_LOOKUP_PROPERTIES[targetType];
  const { data: fetchedLookupProps } = useQuery({
    queryKey: ['csv-import', 'lookup-properties', orgId, targetType],
    queryFn: async () =>
      deriveLookupProperties(await fetchPropertyTypes(orgId, targetType)),
    enabled: Boolean(targetType) && !staticLookupProps,
    staleTime: 5 * 60 * 1000,
  });

  const columnItems = useMemo(
    () => [
      { label: 'Select column', id: '' },
      ...sourceColumns.map(col => ({ label: col, id: col })),
    ],
    [sourceColumns]
  );

  const entityTypeItems = useMemo(
    () => [
      { label: 'Select object type', id: '' },
      ...targetTypeOptions.map(t => ({ label: t.label, id: t.value })),
    ],
    [targetTypeOptions]
  );

  const propertyItems = useMemo(() => {
    const props =
      staticLookupProps ?? fetchedLookupProps ?? EMPTY_LOOKUP_PROPERTIES;
    return [
      { label: 'Select property', id: '' },
      ...props.map(p => ({ label: p.label, id: p.value })),
    ];
  }, [staticLookupProps, fetchedLookupProps]);

  const handleSourceColumnChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onUpdate(index, {
          ...mappingRef.current,
          source_column: String(key),
        });
      }
    },
    [onUpdate, index]
  );

  const handleEntityTypeChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onUpdate(index, {
          ...mappingRef.current,
          target_entity_type_name: String(key),
          target_property: '',
        });
      }
    },
    [onUpdate, index]
  );

  const handlePropertyChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onUpdate(index, {
          ...mappingRef.current,
          target_property: String(key),
        });
      }
    },
    [onUpdate, index]
  );

  const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

  return (
    <div className='py-2 border-t border-border-tertiary first:border-t-0'>
      <div className='flex flex-col sm:grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-stretch sm:items-end'>
        <div className='min-w-0'>
          <Select
            value={mapping.source_column}
            onChange={handleSourceColumnChange}
            size='xs'
            items={columnItems}
            aria-label={vocabulary.columnPickerAriaLabel}
            label={vocabulary.columnPickerLabel}
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
        <div className='min-w-0'>
          <Select
            value={mapping.target_entity_type_name}
            onChange={handleEntityTypeChange}
            size='xs'
            items={entityTypeItems}
            aria-label='Target object type'
            label='Link to'
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
        <div className='min-w-0'>
          <Select
            value={mapping.target_property}
            onChange={handlePropertyChange}
            size='xs'
            isDisabled={!mapping.target_entity_type_name}
            items={propertyItems}
            aria-label='Lookup property'
            label='Match by'
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
        <IconButton
          icon={Trash01}
          tooltip='Remove association'
          size='xs'
          color='tertiary'
          onClick={handleRemove}
          className='mb-0.5'
        />
      </div>
    </div>
  );
});

// ============================================================================
// InlineCrmEntityAssociationsSection
// ============================================================================

export interface InlineCrmEntityAssociationsSectionProps {
  /** All CRM entity association mappings. */
  mappings: CrmEntityAssociationMapping[];
  /** Source file columns available for the active file. */
  sourceColumns: string[];
  /** Source file ID for new mappings. */
  sourceFileId: string | number;
  /** Org id, for fetching custom-type lookup property schemas. */
  orgId: number;
  /** Schema-driven target entity types (core + custom domain types). */
  targetTypeOptions: ImportTypeOption[];
  /** Called when mappings change. */
  onMappingsChange: (mappings: CrmEntityAssociationMapping[]) => void;
}

export function InlineCrmEntityAssociationsSection({
  mappings,
  sourceColumns,
  sourceFileId,
  orgId,
  targetTypeOptions,
  onMappingsChange,
}: InlineCrmEntityAssociationsSectionProps) {
  // Stable numeric IDs for CRM entity association rows — persists across
  // re-renders so React can reconcile correctly when rows are added/removed.
  const crmAssocIds = useRef<number[]>([]);
  const nextCrmAssocId = useRef(0);

  // Filter to only mappings for this source file
  const fileMappings = useMemo(() => {
    const filtered = mappings
      .map((m, i) => ({ mapping: m, globalIndex: i }))
      .filter(
        ({ mapping }) => String(mapping.source_file_id) === String(sourceFileId)
      );
    // Ensure stable IDs array is long enough for all mappings
    while (crmAssocIds.current.length < mappings.length) {
      crmAssocIds.current.push(nextCrmAssocId.current++);
    }
    return filtered;
  }, [mappings, sourceFileId]);

  const handleUpdate = useCallback(
    (globalIndex: number, updated: CrmEntityAssociationMapping) => {
      const next = [...mappings];
      next[globalIndex] = updated;
      onMappingsChange(next);
    },
    [mappings, onMappingsChange]
  );

  const handleRemove = useCallback(
    (globalIndex: number) => {
      onMappingsChange(mappings.filter((_, i) => i !== globalIndex));
    },
    [mappings, onMappingsChange]
  );

  const handleAdd = useCallback(() => {
    onMappingsChange([
      ...mappings,
      {
        source_file_id: sourceFileId,
        source_column: '',
        target_entity_type_name: '',
        target_property: '',
      },
    ]);
  }, [mappings, sourceFileId, onMappingsChange]);

  return (
    <div className='space-y-2'>
      <div>
        <p className='text-caption text-fg-quaternary'>
          Link to existing CRM records
        </p>
        <p className='text-xs text-fg-tertiary mt-0.5'>
          Match a CSV column to an existing object (e.g. assign an owner by
          email).
        </p>
      </div>
      <div className='border border-border-secondary rounded-lg bg-bg-primary p-3'>
        {fileMappings.length > 0 && (
          <ScrollableTableWrapper>
            <div className='min-w-[400px]'>
              {fileMappings.map(({ mapping, globalIndex }) => (
                <CrmEntityAssociationRow
                  key={`crm-assoc-${crmAssocIds.current[globalIndex] ?? globalIndex}`}
                  index={globalIndex}
                  mapping={mapping}
                  sourceColumns={sourceColumns}
                  orgId={orgId}
                  targetTypeOptions={targetTypeOptions}
                  onUpdate={handleUpdate}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          </ScrollableTableWrapper>
        )}
        <Button
          type='button'
          color='link-color'
          size='sm'
          onClick={handleAdd}
          className={fileMappings.length > 0 ? 'mt-2' : ''}
        >
          + Link to existing record
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// ColumnLinkConfig
// ============================================================================

export interface ColumnLinkConfigProps {
  /** The link mapping for this column (source column is implicit = the row's column). */
  mapping: CrmEntityAssociationMapping;
  /** Org id, for fetching custom-type lookup property schemas. */
  orgId: number;
  /** Entity type of the file being imported (the association's source side). */
  sourceEntityType: string;
  /** Schema-driven target entity types (core + custom domain types). */
  targetTypeOptions: ImportTypeOption[];
  /** All association types for the org (from useAssociationMatchers). */
  associationTypes: AssociationType[];
  /** Kind-aware source/target matchers from useAssociationMatchers. */
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  /** Called with the updated mapping whenever a field changes. */
  onChange: (mapping: CrmEntityAssociationMapping) => void;
}

/**
 * Inline "Link to record" configuration shown beneath a column row when the
 * column is mapped as an association. The CSV column is implicit (the row's
 * column), so this only configures: Link to (target entity type), Match by
 * (lookup property), and As (association type — surfaced only when the
 * (source, target) pair has more than one association type).
 */
export const ColumnLinkConfig = memo(function ColumnLinkConfig({
  mapping,
  orgId,
  sourceEntityType,
  targetTypeOptions,
  associationTypes,
  matchesSource,
  matchesTarget,
  onChange,
}: ColumnLinkConfigProps) {
  const mappingRef = useRef(mapping);
  // useLayoutEffect (not useEffect): mappingRef must be fresh before the reconcile effect's convergence re-run.
  useLayoutEffect(() => {
    mappingRef.current = mapping;
  }, [mapping]);

  const targetType = mapping.target_entity_type_name;

  // Lookup ("match by") properties: curated static list for core types; derived
  // from the live property schema for custom (org_*) types.
  const staticLookupProps = CRM_ENTITY_LOOKUP_PROPERTIES[targetType];
  const { data: fetchedLookupProps } = useQuery({
    queryKey: ['csv-import', 'lookup-properties', orgId, targetType],
    queryFn: async () =>
      deriveLookupProperties(await fetchPropertyTypes(orgId, targetType)),
    enabled: Boolean(targetType) && !staticLookupProps,
    staleTime: 5 * 60 * 1000,
  });

  const entityTypeItems = useMemo(
    () => [
      { label: 'Select object type', id: '' },
      ...targetTypeOptions.map(t => ({ label: t.label, id: t.value })),
    ],
    [targetTypeOptions]
  );

  const propertyItems = useMemo(() => {
    const props =
      staticLookupProps ?? fetchedLookupProps ?? EMPTY_LOOKUP_PROPERTIES;
    return [
      { label: 'Select property', id: '' },
      ...props.map(p => ({ label: p.label, id: p.value })),
    ];
  }, [staticLookupProps, fetchedLookupProps]);

  // Association types valid for this (source -> target) pair. Kind-aware via the
  // matchers, so kind-based associations (notes ↔ contacts, etc.) are included.
  const pairTypes = useMemo(() => {
    if (!sourceEntityType || !targetType) {
      return EMPTY_PAIR_TYPES;
    }
    return associationTypes.filter(
      at => matchesSource(at, sourceEntityType) && matchesTarget(at, targetType)
    );
  }, [
    associationTypes,
    matchesSource,
    matchesTarget,
    sourceEntityType,
    targetType,
  ]);

  // Keep association_type_name consistent with the pair using a single validity
  // rule. Exactly one valid type -> auto-select it. Otherwise (0 or >1 valid
  // types) drop a stored value that is no longer valid for this pair: the value
  // can go stale when associationTypes/matchers update or the source kind is
  // reclassified, and the "As" select would then bind an out-of-options value
  // that buildSubmitPayload's target-only filter does not catch. When >1 valid
  // types remain the user re-picks via the "As" select below.
  useEffect(() => {
    const { current } = mappingRef;
    const selected = current.association_type_name;

    if (pairTypes.length === 1) {
      // Unambiguous pair: auto-select the only valid type.
      const only = pairTypes[0].association_type_name;
      if (selected !== only) {
        onChange({ ...current, association_type_name: only });
      }
      return;
    }

    // 0 or >1 valid types: clear a selection that is no longer valid for this
    // pair. Guarded by `selected && !valid` so the forced re-run (onChange is
    // NOT a stable reference) is a no-op once cleared -> converges, no loop.
    if (
      selected &&
      !pairTypes.some(at => at.association_type_name === selected)
    ) {
      const { association_type_name: _removed, ...rest } = current;
      onChange(rest);
    }
  }, [pairTypes, onChange]);

  const associationTypeItems = useMemo(
    () => [
      { label: 'Select association', id: '' },
      ...pairTypes.map(at => ({
        label: at.label,
        id: at.association_type_name,
      })),
    ],
    [pairTypes]
  );

  const handleEntityTypeChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onChange({
          ...mappingRef.current,
          target_entity_type_name: String(key),
          target_property: '',
          association_type_name: undefined,
        });
      }
    },
    [onChange]
  );

  const handlePropertyChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onChange({ ...mappingRef.current, target_property: String(key) });
      }
    },
    [onChange]
  );

  const handleAssociationTypeChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onChange({
          ...mappingRef.current,
          association_type_name: String(key) || undefined,
        });
      }
    },
    [onChange]
  );

  const showAssociationSelect = pairTypes.length > 1;

  return (
    <div className='rounded-lg border border-border-secondary bg-bg-secondary p-3 space-y-2'>
      <p className='text-xs text-fg-tertiary'>
        Matches existing records and others in this import.
      </p>
      <div className='flex flex-col sm:flex-row gap-2 sm:items-end'>
        <div className='min-w-0 flex-1'>
          <Select
            value={mapping.target_entity_type_name}
            onChange={handleEntityTypeChange}
            size='xs'
            items={entityTypeItems}
            aria-label='Target object type'
            label='Link to'
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
        <div className='min-w-0 flex-1'>
          <Select
            value={mapping.target_property}
            onChange={handlePropertyChange}
            size='xs'
            isDisabled={!mapping.target_entity_type_name}
            items={propertyItems}
            aria-label='Lookup property'
            label='Match by'
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>
        {showAssociationSelect && (
          <div className='min-w-0 flex-1'>
            <Select
              value={mapping.association_type_name ?? ''}
              onChange={handleAssociationTypeChange}
              size='xs'
              items={associationTypeItems}
              aria-label='Association type'
              label='As'
            >
              {item => <SelectItem {...item} />}
            </Select>
          </div>
        )}
      </div>
      {(!targetType || !mapping.target_property) && (
        <p className='text-xs text-error-primary'>
          {!targetType
            ? 'Choose what this column links to, or switch it back to a property.'
            : 'Choose a property to match on to complete this link.'}
        </p>
      )}
    </div>
  );
});
